import * as z from 'zod/v4-mini';

import { type PreviewExperimentsRequestBody } from './aliases';
import type { OpzlyExperimentConfig } from './types';

/**
 * Define all of your JIT enabled experiments and variants here.
 *
 * The only requirement is that each one has the 'control' field
 * which should be the name of the control group set up in opzly.
 *
 * You can optionally define your variants. There is no strict
 * naming convention, as these are only to be used in your feat
 * code, e.g. `is_exp_enabled === THIS_CONST.my_exp.my_variant`
 */
export const VARIANT_IDENTIFIER = 'variant';
export const VARIANT_A_IDENTIFIER = 'varianta';
export const VARIANT_B_IDENTIFIER = 'variantb';
export const ENABLED_IDENTIFIER = 'enabled';
export const CONTROL_IDENTIFIER = 'control';
export const ON_IDENTIFIER = 'on';
export const OFF_IDENTIFIER = 'off';
export const ZENDESK_EXPERIMENT_TAG = 'zendesk';

export const OPTIMIZELY_EXPERIMENTS = {
  test_experiment: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_5648_adobe_landing: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_productview_derived_metrics_test: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  sxp_listing_repop_photos: {
    control: CONTROL_IDENTIFIER,
    // This is a cross-platform flag so uses naming conventions defined by iOS
    on: 'on',
    off: 'off',
  },
  web_6304_show_suggested_offer_in_pdp: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_6746_search_for_sellers: {
    control: CONTROL_IDENTIFIER,
    variantA: VARIANT_A_IDENTIFIER,
    variantB: VARIANT_B_IDENTIFIER,
  },
  web_6874_index_driven_suggested_offers: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_7019_visible_search_bar_mweb: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_qr_code_shop_2025_q3: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_portrait_product_card: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_7217_tease_sfy_search: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_6599_search_bar_ds_rollout: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_au_shipping_phase_1: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  ppx_399_buyer_actions_vacation_mode: {
    control: OFF_IDENTIFIER,
    variant: ON_IDENTIFIER,
  },
  web_7707_broaden_appeal_imagery: {
    control: CONTROL_IDENTIFIER,
    variantA: VARIANT_A_IDENTIFIER,
    variantB: VARIANT_B_IDENTIFIER,
  },
  web_8308_price_estimator: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_7403_csv_enable_feature_flag: {
    control: CONTROL_IDENTIFIER,
    enabled: ENABLED_IDENTIFIER,
  },
  parcel_weight_ml_v2_at_listing: {
    control: OFF_IDENTIFIER,
    on: ON_IDENTIFIER,
  },
  parcel_weight_ml_v2_adherence: {
    control: OFF_IDENTIFIER,
    on: ON_IDENTIFIER,
  },
  boosted_listing_fee_increase_uk: {
    control: CONTROL_IDENTIFIER,
    off: OFF_IDENTIFIER,
    on: ON_IDENTIFIER,
  },
  boosted_listing_fee_increase_us: {
    control: CONTROL_IDENTIFIER,
    off: OFF_IDENTIFIER,
    on: ON_IDENTIFIER,
  },
  web_7634_discount_all_shop_panel: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  ppx_outfit_image_backgrounds: {
    control: OFF_IDENTIFIER,
    variant: ON_IDENTIFIER,
  },
  web_upfront_shipping_banner: {
    control: CONTROL_IDENTIFIER,
    off: OFF_IDENTIFIER,
    on: ON_IDENTIFIER,
  },
  enable_magic_link_delay_mitigation: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8248_well_known_brands_homepage: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  ppx_1074_logged_out_drop: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  zendesk_chat_with_fin: {
    control: CONTROL_IDENTIFIER,
    off: OFF_IDENTIFIER,
    on: ON_IDENTIFIER,
    metadata: {
      tags: [ZENDESK_EXPERIMENT_TAG],
      public_alias: 'chat',
    },
  },
  web_address_validation_buyer: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_address_validation_seller: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8411_search_on_sold_username: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8309_webp_product_image: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8744_bespoke_pla_pdp_condensed_v2: {
    control: CONTROL_IDENTIFIER,
    variantA: VARIANT_A_IDENTIFIER,
    variantB: VARIANT_B_IDENTIFIER,
  },
  web_8639_guest_free_shipping_banner: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
    variables: z.object({ campaign_end_copy: z.string() }),
  },
  web_8352_stats_v2: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_se_12195_stats_dynamic_copy: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  existing_address_validation_buyer_web: {
    control: CONTROL_IDENTIFIER,
    off: OFF_IDENTIFIER,
    on: ON_IDENTIFIER,
  },
  existing_address_validation_seller_web: {
    control: CONTROL_IDENTIFIER,
    off: OFF_IDENTIFIER,
    on: ON_IDENTIFIER,
  },
  web_variables_test: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8688_signup_wall_simplified_header: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8669_outfits_homepage_upsell: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_se_12157_import_page: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_12096_todo_with_illustrations: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8765_free_shipping_messaging_us: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_username_change_prompt: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8685_app_download_burger_menu: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8651_buy_now_presignup: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8808_make_offer_cta_app_download: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8958_increase_products_borv_modal: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8882_purchase_confirmation_screen_refresh: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_se_12625_reprice_and_relist: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  depop_shipping_australia: {
    control: CONTROL_IDENTIFIER,
    off: OFF_IDENTIFIER,
    // Remove this variations from OPTIMIZELY_EXPERIMENT_OPTIONS when this experiment is rolled out
    manual_shipping_optional: 'manual_shipping_optional',
    depop_shipping_only: 'depop_shipping_only',
    depop_shipping_onboarding: 'depop_shipping_onboarding',
  },
  web_se_onboarding_address_checkbox: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  nse_address_collection_on_label_generation: {
    control: CONTROL_IDENTIFIER,
    off: OFF_IDENTIFIER,
    on: ON_IDENTIFIER,
  },
  web_age_verification_signup: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_9027_filterable_ymal_on_condensed_pla: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_9191_6_products_per_row_filterable_ymal: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_9227_filterable_ymal_internal_landers: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_thrift_ui_button: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_se_12728_stats_overview_module: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8980_visual_search_fake_door: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_8983_saved_search_fake_door: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_9084_homepage_red_grey_header: {
    control: CONTROL_IDENTIFIER,
    variantA: VARIANT_A_IDENTIFIER,
    variantB: VARIANT_B_IDENTIFIER,
  },
  web_12780_repop_selling_module: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_12775_in_price_range_prompt: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_9158_remove_username_from_signup: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_12803_repop_todo_module: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_12804_repop_purchase_page: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_9153_bespoke_condensed_pdp: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_12882_auto_send_offers: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_9149_style_quiz_signup_gate: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
    variables: z.object({ title: z.string(), description: z.string() }),
  },
  web_9180_remove_pdp_save_icon: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_9163_app_download_cta_on_homepage: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  shipping_under_10_discounted: {
    control: CONTROL_IDENTIFIER,
    on: ON_IDENTIFIER,
    off: OFF_IDENTIFIER,
  },
  tse_ghost_seller_buyer_friction_v1: {
    control: OFF_IDENTIFIER,
    variantA: VARIANT_A_IDENTIFIER,
    variantB: VARIANT_B_IDENTIFIER,
  },
  web_12998_description_ml_listing: {
    control: CONTROL_IDENTIFIER,
    variantA: VARIANT_A_IDENTIFIER,
    variantB: VARIANT_B_IDENTIFIER,
  },
  web_9228_bag_signup_prompt: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_13003_listing_enhancements: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_9278_ebay_sso: {
    control: CONTROL_IDENTIFIER,
    on: ON_IDENTIFIER,
    off: OFF_IDENTIFIER,
  },
  web_9046_homepage_marketing_banner: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_se_13150_pudo: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_9287_combined_signup_checkout_v2: {
    control: CONTROL_IDENTIFIER,
    variantA: VARIANT_A_IDENTIFIER,
    variantB: VARIANT_B_IDENTIFIER,
  },
  web_9275_guest_likes: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  web_9276_like_icon_on_shop_pages: {
    control: CONTROL_IDENTIFIER,
    variant: VARIANT_IDENTIFIER,
  },
  ppx_flexible_campaigns_ui_web: {
    control: CONTROL_IDENTIFIER,
    on: ON_IDENTIFIER,
    off: OFF_IDENTIFIER,
  },
  proof_of_auth_category_expansion: {
    control: OFF_IDENTIFIER,
    variant: ON_IDENTIFIER,
  },
} as const satisfies Record<string, OpzlyExperimentConfig>;

export type OpzlyExperiments = typeof OPTIMIZELY_EXPERIMENTS;
export type OpzlyExperimentName = keyof OpzlyExperiments;

export const PREVIEW_EXPERIMENTS: PreviewExperimentsRequestBody = {
  flags: Object.keys(OPTIMIZELY_EXPERIMENTS).map((k) => ({
    name: k,
  })),
};
